import { useEffect, useRef, useState } from 'react';
import PageContainer from '../../../components/container/PageContainer';
import {
  COST_ACCOUNTING_HEADER,
  COST_ACCOUNTING_MODAL_FIELDS,
} from '../../../constants/costAccounting';
import {
  EmptyCostAccountingGetResponse,
  CostAccountingGetResponse,
} from '../../../types/costAccounting/CostAccountingGetResponse';
import { CostAccountingCsvPreviewItem } from '../../../types/costAccounting/CostAccountingCsvPreviewItem';
import {
  getCostAccountingList,
  exportCostAccountingCsv,
} from '../../../services/api';
import { Col, Row } from 'react-bootstrap';
import RenderCostAccountingRow from './RenderCostAccountingRow';
import TablePagination from '../../../components/table/pagination/TablePagination';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import {
  DefaultCostAccountingGetQueryParams,
  CostAccountingGetQueryParams,
} from '../../../types/costAccounting/CostAccountingGetQueryParams';
import { SortOrder } from '../../../types/sort/sort';
import {
  COST_ACCOUNTING_SORT_ORDER,
  COST_ACCOUNTING_DEFAULT_SORT_COLUMN,
} from '../../../constants/sort';
import CsvModal from '../../../components/modal/CsvModal';
import TokyoProjectSearch from '../../../components/search/TokyoProjectSearch';
import YmdPicker from '../../../components/picker/YmdPicker';
import { format } from 'date-fns';
import SearchResetButtons from '../../../components/button/SearchResetButtons';
import CsvExportButton from '../../../components/button/CsvExportButton';
import ReadOnlyTable from '../../../components/table/ReadOnlyTable';
import { saveBlobAsFile } from '../../../utils/saveBlobAsFile';
import { formatYmdDisplay } from '../../../utils/formatYmdDisplay';
import OfficeTabs from '../../../components/nav/OfficeTabs';
import SyncedScrollHeader from '../../../components/layout/SyncedScrollHeader';
import '../../../styles/CostAccountingHeader.css';

const CostAccounting = () => {
  const [getQueryParams, setGetQueryParams] =
    useState<CostAccountingGetQueryParams>(DefaultCostAccountingGetQueryParams);
  const [sortOrder, setSortOrder] = useState<SortOrder>(
    COST_ACCOUNTING_SORT_ORDER
  );
  const [activeSortColumn, setActiveSortColumn] = useState<string>(
    COST_ACCOUNTING_DEFAULT_SORT_COLUMN
  );
  const [projectLabel, setProjectLabel] = useState<string | null>(null);
  const [csvItem, setCsvItem] = useState<CostAccountingCsvPreviewItem | null>(
    null
  );
  const [showCsvModal, setShowCsvModal] = useState(false);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [controlsKey, setControlsKey] = useState(0);
  const [hasSearched, setHasSearched] = useState(false);
  const queryClient = useQueryClient();
  const fmtYMD = (d: Date | null | undefined) =>
    d ? format(d, 'yyyy-MM-dd') : '';

  // テーブルの横スクロール要素（ヘッダー追従用）
  const tableRef = useRef<HTMLDivElement>(null);
  // ヘッダーのref（高さ計算用）
  const headerRef = useRef<HTMLDivElement>(null);
  // フッターのref（テーブルの高さ計算用）
  const footerRef = useRef<HTMLDivElement>(null);

  const { mutateAsync: exportCsv } = useMutation({
    mutationKey: ['exportCostAccountingCsv'],
    mutationFn: () => exportCostAccountingCsv(getQueryParams),
  });

  // 検索のクエリパラメーターをセット
  const setQuery = (
    patch: {
      selectedDate?: Date | null;
    } & Partial<
      Pick<CostAccountingGetQueryParams, 'startDate' | 'endDate' | 'projectId'>
    >
  ) => {
    setGetQueryParams((prev) => {
      const next = {
        ...prev,
        ...patch,
      } as CostAccountingGetQueryParams;

      return next;
    });
  };

  // 一覧取得
  const { data: costAccountingGetResponse, refetch } =
    useQuery<CostAccountingGetResponse>({
      queryKey: ['costAccountingList'],
      queryFn: () => getCostAccountingList(getQueryParams),
      initialData: EmptyCostAccountingGetResponse,
      // 初期表示でデータを拾わない（検索ボタン押下までフェッチしない）
      enabled: hasSearched,
      // エラー時に自動リトライしない（待ち時間を防止）
      retry: false,
    });

  // 合計値
  const totalsRaw = hasSearched ? costAccountingGetResponse.totalItems : null;
  const totals =
    totalsRaw == null
      ? null
      : Array.isArray(totalsRaw)
        ? (totalsRaw[0] ?? null)
        : totalsRaw;

  // ページ数、ソート順、ソートカラムが変わったら一覧を再取得
  useEffect(() => {
    queryClient.invalidateQueries({
      queryKey: ['costAccountingList'],
    });
  }, [getQueryParams.page, getQueryParams.sortKey, getQueryParams.sortOrder]);

  // ソートの状態を管理
  const handleSort = (sortKey: string) => {
    if (sortKey === activeSortColumn) {
      // 選択中のカラムが現在のアクティブなカラムと同じ場合、ソート順を切り替える
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      // 新しいカラムが選択された場合、アクティブなカラムを降順で更新
      setActiveSortColumn(sortKey);
      setSortOrder('desc');
    }
  };

  // ソートの状態が変わったときにクエリパラメータを更新
  useEffect(() => {
    setGetQueryParams((prev) => ({
      ...prev,
      sortOrder: sortOrder,
      sortKey: activeSortColumn,
    }));
  }, [sortOrder, activeSortColumn]);

  // 期間入力チェック（片側のみ選択 or 逆転している場合はNG）
  const validateRange = () => {
    const start = !!getQueryParams.startDate;
    const end = !!getQueryParams.endDate;
    if (start !== end) {
      alert('期間は開始日と終了日を両方選択してください');
      return false;
    }
    if (
      getQueryParams.startDate &&
      getQueryParams.endDate &&
      getQueryParams.startDate > getQueryParams.endDate
    ) {
      alert('開始日は終了日以前にしてください');
      return false;
    }
    return true;
  };

  // CSV出力用のモーダル情報を作成
  const buildCsvItem = (): CostAccountingCsvPreviewItem => {
    const startDate = formatYmdDisplay(getQueryParams.startDate) || '全期間';
    const endDate = formatYmdDisplay(getQueryParams.endDate) || '全期間';

    const projectName =
      getQueryParams.projectId != null
        ? (projectLabel ?? '')
        : '全プロジェクト';

    return { startDate, endDate, projectName };
  };

  const onCsvButtonClick = async () => {
    if (!validateRange()) return;
    setCsvItem(buildCsvItem());
    setShowCsvModal(true);
  };

  const onCsvModalSubmitButtonClick = async () => {
    try {
      const { blob, filename } = await exportCsv();

      const fallback = 'manhours.csv';
      const resolved =
        filename && filename.trim() ? decodeURIComponent(filename) : fallback;

      const ok = await saveBlobAsFile(blob, resolved);
      if (ok) alert('CSVファイルの保存が完了しました');
    } catch (err: any) {
      let msg = '';
      const data = err?.response?.data;
      if (data instanceof Blob) {
        const text = await data.text();
        const json = JSON.parse(text);
        if (json?.message) msg = json.message;
      }
      alert(msg);
    }
  };

  return (
    <PageContainer title="原価計算管理">
      <SyncedScrollHeader
        targetRef={tableRef}
        className="mb-3"
        outerRef={headerRef}
      >
        <div className="align-self-start w-100">
          <OfficeTabs />
        </div>
        <Row className="header-row-ccostaccounting gx-0">
          {/* 左：検索条件 */}
          <Col className="header-fields-ccostaccounting order-1">
            <TokyoProjectSearch
              key={`project-${controlsKey}`}
              setSelectedId={(p: number) => setQuery({ projectId: p })}
              onSelectedLabel={(label) => setProjectLabel(label)}
            />
            <YmdPicker
              placeholder="期間を選択"
              selectedDate={startDate}
              setSelectedDate={(d) => {
                setStartDate(d);
                setQuery({ startDate: fmtYMD(d) });
              }}
            />
            <span>〜</span>
            <YmdPicker
              placeholder="期間を選択"
              selectedDate={endDate}
              setSelectedDate={(d) => {
                setEndDate(d);
                setQuery({ endDate: fmtYMD(d) });
              }}
            />
          </Col>
          {/* 右：ボタン群（検索/リセット固定 + CSVは折りたたみ対象） */}
          <Col xs="auto" className="header-right-ccostaccounting order-2">
            <div className="actions-group-ccostaccounting">
              <div className="actions-searchreset-ccostaccounting">
                <SearchResetButtons
                  onSearch={async () => {
                    if (!validateRange()) return;
                    try {
                      // 検索ボタン押下時はページを1に戻す
                      setGetQueryParams((prev) => ({ ...prev, page: 1 }));
                      setHasSearched(true);
                      const res = await refetch();
                      if (res.error) {
                        const data: any = (res.error as any)?.response?.data;
                        const msg =
                          typeof data === 'string'
                            ? data
                            : (data?.message ??
                              (res.error as any)?.message ??
                              'エラーが発生しました');
                        alert(msg);
                        setHasSearched(false);
                        return;
                      }
                      if (!res.data?.items?.length) {
                        setHasSearched(false);
                        alert('該当データが存在しません');
                      }
                    } catch (err: any) {
                      setHasSearched(false);
                      alert(err?.response?.data?.message);
                    }
                  }}
                  onReset={() => {
                    setStartDate(null);
                    setEndDate(null);
                    setProjectLabel(null);
                    setGetQueryParams(DefaultCostAccountingGetQueryParams);
                    setControlsKey((k) => k + 1);
                    setHasSearched(false);
                  }}
                />
              </div>

              {/* 折りたたみ対象（CSV） */}
              <div className="actions-extra-ccostaccounting">
                <CsvExportButton
                  onClick={onCsvButtonClick}
                  buttonLabel="CSV出力"
                />
              </div>
            </div>
          </Col>
        </Row>
      </SyncedScrollHeader>
      <ReadOnlyTable
        headers={COST_ACCOUNTING_HEADER}
        items={hasSearched ? costAccountingGetResponse.items : []}
        renderItem={(item) => <RenderCostAccountingRow item={item} />}
        onSort={handleSort}
        sortOrder={sortOrder}
        activeSortColumn={activeSortColumn}
        actionable={false}
        scrollContainerRef={tableRef}
        headerRef={headerRef}
        footerRef={footerRef}
        renderFooter={() =>
          totals ? (
            <tr className="table-secondary fw-bold">
              <td colSpan={4} className="text-end">
                合計
              </td>
              <td>{totals.totalTotalCost.toLocaleString('ja-JP')}</td>
              <td>{totals.totalMahHoursCost.toLocaleString('ja-JP')}</td>
              <td>
                {totals.totalDairenSubcontractingCost.toLocaleString('ja-JP')}
              </td>
              <td>
                {totals.totalOtherSubcontractingCost.toLocaleString('ja-JP')}
              </td>
              <td>{totals.totalExpenses.toLocaleString('ja-JP')}</td>
              <td>{totals.totalManHours}</td>
            </tr>
          ) : null
        }
      />

      <TablePagination
        totalPages={hasSearched ? costAccountingGetResponse.totalPages : 1}
        onPageChange={(page) => {
          setGetQueryParams((prev) => ({ ...prev, page }));
        }}
        ref={footerRef}
      />

      {showCsvModal && csvItem && (
        <CsvModal<CostAccountingCsvPreviewItem>
          show={showCsvModal}
          item={csvItem}
          fields={COST_ACCOUNTING_MODAL_FIELDS}
          saveButtonLabel="OK"
          onClose={() => {
            setShowCsvModal(false);
            setCsvItem(null);
          }}
          onConfirm={async () => {
            await onCsvModalSubmitButtonClick();
            setShowCsvModal(false);
            setCsvItem(null);
          }}
        />
      )}
    </PageContainer>
  );
};

export default CostAccounting;
